import logger from '@/utils/logger'
import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

import {getField, updateField} from 'vuex-map-fields'
import {HTTP} from '@/services/repository'
import ecommerceApiService from '@/services/ecommerceApi'
import { getProductImageUrl as imageUtilsGetProductImageUrl, getCategoryImageUrl as imageUtilsGetCategoryImageUrl } from '@/utils/imageUtils'

Vue.use(Vuex)

// Helper function to get current school identifier for cart isolation
function getCurrentSchool() {
    // Check URL parameter first (for local dev with ?theme=)
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    const schoolMap = ['westmoreland', 'windermere', 'test', 'coastalab', 'demo'];
    if (themeParam && schoolMap.includes(themeParam.toLowerCase())) {
        return themeParam.toLowerCase();
    }

    // Check cookie (set by index.html script)
    const cookieMatch = document.cookie.match(/ecommerce_school=([^;]+)/);
    if (cookieMatch) {
        return cookieMatch[1];
    }

    // Fallback to checking URL path
    const pathSegments = window.location.pathname.toLowerCase().split('/').filter(s => s.length > 0);
    if (pathSegments.length > 0 && schoolMap.includes(pathSegments[0])) {
        return pathSegments[0];
    }

    // Default to westmoreland if no match
    return 'westmoreland';
}

// Helper function to get school-specific localStorage key
function getSchoolCartKey(suffix = '') {
    const school = getCurrentSchool();
    return suffix ? `ecommerce-cart-${school}-${suffix}` : `ecommerce-cart-${school}`;
}

// Cart storage version - increment to force reset of stale cached data
// v2: Fix for stale tax data showing shipping amount as tax (2026-01-20)
const CART_STORAGE_VERSION = 2;

// Feature flag configuration module
const configModule = {
    namespaced: true,
    state: {
        features: {},
        loaded: false,
        loading: false,
        authenticationMode: 'LOGIN_AND_GUEST',
        error: null,
        lastFetched: null
    },
    mutations: {
        SET_LOADING(state, loading) {
            state.loading = loading;
        },
        SET_FEATURES(state, features) {
            state.features = features;
            state.loaded = true;
            state.loading = false;
            state.error = null;
            state.lastFetched = new Date().toISOString();
        },
        SET_AUTH_MODE(state, mode) {
            state.authenticationMode = mode;
        },
        SET_ERROR(state, error) {
            state.error = error;
            state.loading = false;
        },
        RESET_CONFIG(state) {
            state.features = {};
            state.loaded = false;
            state.loading = false;
            state.error = null;
            state.lastFetched = null;
        }
    },
    actions: {
        async loadConfiguration({ commit, state }) {
            // Don't reload if already loaded recently (within 5 minutes)
            if (state.loaded && state.lastFetched) {
                const lastFetchTime = new Date(state.lastFetched);
                const now = new Date();
                const timeDiff = (now - lastFetchTime) / 1000 / 60; // minutes
                if (timeDiff < 5) {
                    logger.debug('🔧 Feature flags already loaded recently, skipping reload');
                    return { success: true, cached: true };
                }
            }

            commit('SET_LOADING', true);

            try {
                logger.debug('🔧 Loading feature flags from API...');
                const configUrl = `${window.VUE_CONFIG_API_ECOMMERCE}config`;

                const response = await fetch(configUrl, {
                    method: 'GET',
                    headers: {
                        'X-API-Key': window.ECOMMERCE_API_KEY,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const config = await response.json();

                logger.debug('🔧 Feature flags API response:', config);

                if (config.Successful || config.successful) {
                    // Merge API features with window config overrides
                    // Window config takes precedence for client-specific settings (e.g., Windermere shipping-only)
                    const windowDefaults = getDefaultFeatureFlags();
                    const mergedFeatures = {
                        ...config.features,
                        // Override with window config if explicitly set
                        showPickupLocations: window.SHOW_PICKUP_LOCATIONS !== undefined ? windowDefaults.showPickupLocations : config.features.showPickupLocations,
                        showShippingForm: window.SHOW_SHIPPING_FORM !== undefined ? windowDefaults.showShippingForm : config.features.showShippingForm,
                        pickupLocationsEnabled: window.PICKUP_LOCATIONS_ENABLED !== undefined ? windowDefaults.pickupLocationsEnabled : config.features.pickupLocationsEnabled,
                        shippingEnabled: window.SHIPPING_ENABLED !== undefined ? windowDefaults.shippingEnabled : config.features.shippingEnabled
                    };
                    commit('SET_FEATURES', mergedFeatures);
                    commit('SET_AUTH_MODE', config.authenticationMode || 'LOGIN_AND_GUEST');
                    logger.debug('✅ Feature flags loaded successfully (with window overrides):', mergedFeatures);
                    return { success: true };
                } else {
                    throw new Error(config.message || 'Failed to load configuration');
                }
            } catch (error) {
                logger.warn('⚠️ Failed to load feature flags (non-critical):', error);
                commit('SET_ERROR', error.message);

                // Use safe defaults on error
                const defaultFeatures = getDefaultFeatureFlags();
                commit('SET_FEATURES', defaultFeatures);
                logger.debug('🔧 Using default feature flags due to API error:', defaultFeatures);

                return { success: false, error: error.message, usingDefaults: true };
            }
        },

        async refreshConfiguration({ dispatch }) {
            logger.debug('🔄 Refreshing feature flags...');
            return await dispatch('loadConfiguration');
        }
    },
    getters: {
        isFeatureEnabled: (state) => (featureName) => {
            return state.features[featureName] || false;
        },
        shouldShowLoginButton: (state) => state.features.showLoginButton || false,
        shouldShowGuestCheckout: (state) => state.features.showGuestCheckout || false,
        shouldShowPickupLocations: (state) => state.features.showPickupLocations || false,
        shouldShowShippingForm: (state) => state.features.showShippingForm || false,
        isShippingEnabled: (state) => state.features.shippingEnabled || false,
        isLoginEnabled: (state) => state.features.loginEnabled || false,
        isGuestCheckoutEnabled: (state) => state.features.guestCheckoutEnabled || false,
        isEmailVerificationEnabled: (state) => state.features.emailVerificationEnabled || false,
        isPickupLocationsEnabled: (state) => state.features.pickupLocationsEnabled || false,
        authenticationMode: (state) => state.authenticationMode,
        configLoaded: (state) => state.loaded,
        configLoading: (state) => state.loading,
        configError: (state) => state.error
    }
};

// Default feature flags function - reads from window config if available
function getDefaultFeatureFlags() {
    // Check window config for overrides (from config.*.js files)
    const parseWindowBool = (value, defaultValue) => {
        if (value === undefined || value === null) return defaultValue;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') return value.toLowerCase() === 'true';
        return !!value;
    };

    return {
        loginEnabled: true,
        guestCheckoutEnabled: true,
        emailVerificationEnabled: true,
        categoryAuthRequired: false,
        showLoginButton: true,
        showGuestCheckout: true,
        // Read pickup/shipping settings from window config (for Windermere shipping-only)
        showPickupLocations: parseWindowBool(window.SHOW_PICKUP_LOCATIONS, true),
        showShippingForm: parseWindowBool(window.SHOW_SHIPPING_FORM, false),
        pickupLocationsEnabled: parseWindowBool(window.PICKUP_LOCATIONS_ENABLED, true),
        shippingEnabled: parseWindowBool(window.SHIPPING_ENABLED, false),
        emailNotificationsEnabled: false,
        secureReceiptTokens: true,
        apiKeyRequired: true
    };
}

// Helper functions for API calls
const getFailureResponse = (message) => {
    return {
        'Successful': false,
        'Message': message
    }
}

const callAPI = async (req, method) => {
    try {
        logger.debug(`🔄 API Call: ${method}`, {
            url: `${window.VUE_CONFIG_API}${method}`,
            request: req,
            headers: HTTP.defaults.headers.post
        })

        const response = await HTTP.post(method, req)

        logger.debug(`✅ API Response: ${method}`, {
            status: response.status,
            data: response.data,
            headers: response.headers
        })

        if (response.data.Successful === false) {
            logger.debug(`❌ API Failed: ${method}`, response.data.RequestException)
            return getFailureResponse(response.data.RequestException.Message)
        }
        return response.data
    } catch (e) {
        logger.debug(`💥 API Error: ${method}`, {
            message: e.message,
            response: e.response && e.response.data,
            status: e.response && e.response.status,
            headers: e.response && e.response.headers
        })
        return getFailureResponse(e.message)
    }
}

// GET API method for enhanced repository compatibility
const callAPIGet = async (endpoint) => {
    try {
        logger.debug(`🔄 API GET Call: ${endpoint}`, {
            url: `${window.VUE_CONFIG_API}${endpoint}`,
            headers: HTTP.defaults.headers.post
        })

        const response = await HTTP.get(endpoint)

        logger.debug(`✅ API GET Response: ${endpoint}`, {
            status: response.status,
            data: response.data,
            headers: response.headers
        })

        if (response.data.Successful === false) {
            logger.debug(`❌ API GET Failed: ${endpoint}`, response.data.RequestException)
            return getFailureResponse(response.data.RequestException.Message)
        }
        return response.data
    } catch (e) {
        logger.debug(`💥 API GET Error: ${endpoint}`, {
            message: e.message,
            response: e.response && e.response.data,
            status: e.response && e.response.status,
            headers: e.response && e.response.headers
        })
        return getFailureResponse(e.message)
    }
}
export const store = new Vuex.Store({
    modules: {
        config: configModule
    },
    state:{
        //general
        username:'',
        oldpassword:'',
        password:'',
        loggedInUser:{
            name:'User',
            isAdmin:false,
            walletId:0,
            user_accountId:0,
            user_personId:0,
            badgeId:0,
            LocationId:0,
            role: null, // For role-based pricing
            email: ''
        },
        loggedIn:false,
        showHeader:true,
        robot:true,

        // New UI state
        showLoginModal: false,
        isGuestUser: true,
        loginRedirectUrl: null, // Store where to redirect after login

        lastRefreshDateTime_Wallet:'',
        lastRefreshDateTime_Explorer:'',

        //wallet
        currentWallet:{},
        currentNav:'',
        photoRequirements:'',
        logoOverride:'',
        logoImageBasePath:'',
        requireLogin:false, // Changed to false to allow guest browsing

        pleaseWait:false,
        message:'',

        // Shopping cart for guest users
        cart: {
            items: [],
            total: 0,
            guestInfo: {
                firstName: '',
                lastName: '',
                email: '',
                phone: ''
            },
            // Backend-calculated tax information
            taxAmount: 0,
            totalWithTax: 0,
            // Pickup location information
            selectedPickupLocation: null
        },

        // Email validation and user existence checking
        emailValidation: {
            isChecking: false,
            lastCheckedEmail: '',
            emailExists: false,
            existingUserInfo: null,
            validationError: null,
            showWelcomeBack: false,
            showNewUserGuidance: false
        },

        // Declining balance payment state
        decliningBalance: {
            isAvailable: false,
            isLoading: false,
            accounts: [],
            selectedAccountId: null,
            totalBalance: 0,
            lastChecked: null,
            error: null,
            paymentInProgress: false,
            lastPaymentResult: null
        },

        // Ticket operations state (POS Integration) - Added 2026-01-18
        ticket: {
            isLoading: false,
            currentTicket: null,         // Current ticket being created/modified
            lastCreatedTicket: null,     // Last successfully created ticket
            lastRefundTicket: null,      // Last refund ticket created
            paymentItems: [],            // Payment items for split payments
            error: null,
            lastOperation: null          // 'create', 'addPayment', 'refund', 'updateGuest'
        },

        // Pickup location state
        pickupLocations: {
            isLoading: false,
            locations: [],
            selectedLocation: null,
            error: null,
            lastFetched: null
        },

        // Shipping state
        shipping: {
            isLoading: false,
            config: {
                isEnabled: false,
                provider: 'internal',
                flatRateAmount: 0,
                freeShippingThreshold: 0,
                defaultMethod: 'flat_rate'
            },
            rates: [],
            selectedRate: null,
            freeShippingApplied: false,
            error: null,
            lastFetched: null
        },

        // Product browsing state
        categories: [],
        products: [],
        categorizedProducts: [], // Products pre-grouped by category from new API
        selectedCategory: null,
        searchQuery: '',
        showAvailableOnly: false, // Inventory filter
        sortBy: 'name', // 'name', 'price'
        sortOrder: 'asc', // 'asc', 'desc'
        filters: {
            tags: [],
            priceRange: { min: 0, max: 1000 },
            availability: true
        },

        // Category filters for multi-select filtering
        categoryFilters: {
            isLoading: false,
            isCountsLoading: false,  // Loading state for net product counts
            categoryTypes: [],  // Array of { id, name, includeAsFilter, categories: [...] }
            selectedFilters: {}, // { categoryTypeId: [categoryId, categoryId, ...] }
            totalProducts: 0,
            netProductCounts: {},  // { categoryId: count } - dynamic counts based on selection
            error: null
        },

        // PWA state
        pwa: {
            installPromptEvent: null,
            isInstallable: false,
            isInstalled: false,
            updateAvailable: false,
            showInstallPrompt: false,
            showUpdateNotification: false
        },

        // Caching
        filteredProductsCache: null,
        lastFilterState: null,

        //url product selection
        isUrlProduct:false,
        selectedLocation:'',
        selectedProgram:'',
        selectedDate:'',
        selectedTime:'',
        selectedCost:0,
        selectedDeposit:0,

        //account product
        isAccountProduct:false,

        //
        accountTransactionId:-1,
        selectedPaymentAmount:0,

        person:{},

        selectedProductGUID:null,
        selectedProductId:-1
    },

    mutations: {
        updateField,
        resetbeforeapi(state){
            state.pleaseWait=true;
            state.message=''
        },
        // Authentication mutations
        setLoggedIn(state, value) {
            state.loggedIn = value;
            state.isGuestUser = !value;
        },
        setUser(state, user) {
            state.loggedInUser = { ...state.loggedInUser, ...user };
        },
        logout(state) {
            state.loggedIn = false;
            state.isGuestUser = true;
            state.loggedInUser = {
                name:'User',
                isAdmin:false,
                walletId:0,
                user_accountId:0,
                user_personId:0,
                badgeId:0,
                LocationId:0,
                role: null,
                email: ''
            };
            state.cart.items = [];
            state.cart.total = 0;
            state.loginRedirectUrl = null; // Clear redirect URL on logout
        },
        // Cart mutations (persistence handled in actions)
        addToCart(state, product) {
            // 🔍 DEBUGGING: Log detailed cart operation for troubleshooting
            logger.group('🛒 CART DEBUG: Adding product to cart');
            logger.debug('📦 Product being added:', {
                id: product.id,
                name: product.name,
                price: product.price,
                productId: product.productId,
                fullProduct: product
            });
            logger.debug('🛍️ Current cart items before addition:', state.cart.items.map(item => ({
                id: item.id,
                name: item.name,
                quantity: item.quantity,
                price: item.price
            })));

            const existingItem = state.cart.items.find(item => item.id === product.id);

            if (existingItem) {
                logger.debug('✅ Found existing item, incrementing quantity:', {
                    existingId: existingItem.id,
                    existingName: existingItem.name,
                    oldQuantity: existingItem.quantity,
                    newQuantity: existingItem.quantity + 1
                });
                existingItem.quantity += 1;
            } else {
                logger.debug('➕ Adding new item to cart:', {
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    quantity: 1
                });
                state.cart.items.push({ ...product, quantity: 1 });
            }

            state.cart.total = state.cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);

            logger.debug('🛍️ Cart after addition:', {
                itemCount: state.cart.items.length,
                total: state.cart.total,
                items: state.cart.items.map(item => ({
                    id: item.id,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    subtotal: item.price * item.quantity
                }))
            });
            logger.groupEnd();
        },
        removeFromCart(state, productId) {
            state.cart.items = state.cart.items.filter(item => item.id !== productId);
            state.cart.total = state.cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
        },
        updateCartQuantity(state, { productId, quantity }) {
            const item = state.cart.items.find(item => item.id === productId);
            if (item) {
                item.quantity = quantity;
                if (quantity <= 0) {
                    state.cart.items = state.cart.items.filter(item => item.id !== productId);
                }
            }
            state.cart.total = state.cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
        },
        clearCart(state) {
            state.cart.items = [];
            state.cart.total = 0;
            state.cart.taxAmount = 0;
            state.cart.totalWithTax = 0;
            state.cart.selectedPickupLocation = null;
        },
        setTaxInformation(state, { taxAmount, totalWithTax }) {
            state.cart.taxAmount = taxAmount;
            state.cart.totalWithTax = totalWithTax;
        },
        clearTaxInformation(state) {
            // Clear stale tax data when cart/shipping changes
            // Tax will be recalculated at checkout
            state.cart.taxAmount = 0;
            state.cart.totalWithTax = 0;
        },
        // Cart restoration mutation
        restoreCart(state, cartData) {
            if (cartData && cartData.items) {
                state.cart.items = cartData.items;
                state.cart.total = cartData.total || 0;
                state.cart.guestInfo = cartData.guestInfo || {};
                state.cart.taxAmount = cartData.taxAmount || 0;
                state.cart.totalWithTax = cartData.totalWithTax || 0;
                state.cart.selectedPickupLocation = cartData.selectedPickupLocation || null;
                logger.debug('🛒 Cart restored to Vuex store:', {
                    itemCount: state.cart.items.length,
                    total: state.cart.total,
                    hasPickupLocation: !!state.cart.selectedPickupLocation
                });
            }
        },

        // Email validation mutations
        setEmailChecking(state, isChecking) {
            state.emailValidation.isChecking = isChecking;
        },
        setEmailCheckResult(state, { email, exists, userInfo = null, error = null }) {
            state.emailValidation.lastCheckedEmail = email;
            state.emailValidation.emailExists = exists;
            state.emailValidation.existingUserInfo = userInfo;
            state.emailValidation.validationError = error;
            state.emailValidation.showWelcomeBack = exists && userInfo;
            state.emailValidation.showNewUserGuidance = !exists && !error;
        },
        clearEmailValidation(state) {
            state.emailValidation = {
                isChecking: false,
                lastCheckedEmail: '',
                emailExists: false,
                existingUserInfo: null,
                validationError: null,
                showWelcomeBack: false,
                showNewUserGuidance: false
            };
        },

        // Declining balance mutations
        setDecliningBalanceLoading(state, isLoading) {
            state.decliningBalance.isLoading = isLoading;
        },
        setDecliningBalanceAvailable(state, isAvailable) {
            state.decliningBalance.isAvailable = isAvailable;
        },
        setDecliningBalanceAccounts(state, accounts) {
            state.decliningBalance.accounts = accounts;
            state.decliningBalance.totalBalance = accounts.reduce((total, account) => total + account.currentBalance, 0);
            state.decliningBalance.lastChecked = new Date().toISOString();
        },
        setSelectedDecliningBalanceAccount(state, accountId) {
            state.decliningBalance.selectedAccountId = accountId;
        },
        setDecliningBalanceError(state, error) {
            state.decliningBalance.error = error;
        },
        setDecliningBalancePaymentInProgress(state, inProgress) {
            state.decliningBalance.paymentInProgress = inProgress;
        },
        setDecliningBalancePaymentResult(state, result) {
            state.decliningBalance.lastPaymentResult = result;
        },
        clearDecliningBalanceData(state) {
            state.decliningBalance = {
                isAvailable: false,
                isLoading: false,
                accounts: [],
                selectedAccountId: null,
                totalBalance: 0,
                lastChecked: null,
                error: null,
                paymentInProgress: false,
                lastPaymentResult: null
            };
        },

        // Ticket operations mutations (POS Integration) - Added 2026-01-18
        setTicketLoading(state, isLoading) {
            state.ticket.isLoading = isLoading;
        },
        setCurrentTicket(state, ticket) {
            state.ticket.currentTicket = ticket;
        },
        setLastCreatedTicket(state, ticket) {
            state.ticket.lastCreatedTicket = ticket;
            state.ticket.lastOperation = 'create';
        },
        setLastRefundTicket(state, ticket) {
            state.ticket.lastRefundTicket = ticket;
            state.ticket.lastOperation = 'refund';
        },
        addTicketPaymentItem(state, paymentItem) {
            state.ticket.paymentItems.push(paymentItem);
            state.ticket.lastOperation = 'addPayment';
        },
        setTicketError(state, error) {
            state.ticket.error = error;
        },
        clearTicketPaymentItems(state) {
            state.ticket.paymentItems = [];
        },
        clearTicketData(state) {
            state.ticket = {
                isLoading: false,
                currentTicket: null,
                lastCreatedTicket: null,
                lastRefundTicket: null,
                paymentItems: [],
                error: null,
                lastOperation: null
            };
        },

        // Pickup location mutations
        setPickupLocationsLoading(state, isLoading) {
            state.pickupLocations.isLoading = isLoading;
        },
        setPickupLocations(state, locations) {
            state.pickupLocations.locations = locations;
            state.pickupLocations.lastFetched = new Date().toISOString();
            state.pickupLocations.error = null;
        },
        setSelectedPickupLocation(state, location) {
            state.pickupLocations.selectedLocation = location;
            state.cart.selectedPickupLocation = location;
        },
        setPickupLocationsError(state, error) {
            state.pickupLocations.error = error;
        },
        clearPickupLocationData(state) {
            state.pickupLocations = {
                isLoading: false,
                locations: [],
                selectedLocation: null,
                error: null,
                lastFetched: null
            };
            state.cart.selectedPickupLocation = null;
        },

        // Shipping mutations
        setShippingLoading(state, isLoading) {
            state.shipping.isLoading = isLoading;
        },
        setShippingConfig(state, config) {
            state.shipping.config = { ...state.shipping.config, ...config };
        },
        setShippingRates(state, { rates, freeShippingApplied }) {
            state.shipping.rates = rates || [];
            state.shipping.freeShippingApplied = freeShippingApplied || false;
            state.shipping.lastFetched = new Date().toISOString();
            state.shipping.error = null;
        },
        setSelectedShippingRate(state, rate) {
            state.shipping.selectedRate = rate;
        },
        setShippingError(state, error) {
            state.shipping.error = error;
        },
        clearShippingData(state) {
            state.shipping = {
                isLoading: false,
                config: state.shipping.config, // Keep config
                rates: [],
                selectedRate: null,
                freeShippingApplied: false,
                error: null,
                lastFetched: null
            };
        },

        // Product browsing mutations
        setCategories(state, categories) {
            state.categories = categories;
        },
        setCategoryFiltersLoading(state, isLoading) {
            state.categoryFilters.isLoading = isLoading;
        },
        setCategoryFilters(state, { categoryTypes, totalProducts }) {
            state.categoryFilters.categoryTypes = categoryTypes || [];
            state.categoryFilters.totalProducts = totalProducts || 0;
            state.categoryFilters.error = null;
        },
        setCategoryFiltersError(state, error) {
            state.categoryFilters.error = error;
        },
        toggleCategoryFilter(state, { categoryTypeId, categoryId }) {
            if (!state.categoryFilters.selectedFilters[categoryTypeId]) {
                Vue.set(state.categoryFilters.selectedFilters, categoryTypeId, []);
            }
            const filters = state.categoryFilters.selectedFilters[categoryTypeId];
            const index = filters.indexOf(categoryId);
            if (index > -1) {
                filters.splice(index, 1);
            } else {
                filters.push(categoryId);
            }
        },
        clearCategoryFilters(state) {
            state.categoryFilters.selectedFilters = {};
            state.categoryFilters.netProductCounts = {};
        },
        setNetProductCountsLoading(state, isLoading) {
            state.categoryFilters.isCountsLoading = isLoading;
        },
        setNetProductCounts(state, counts) {
            // Transform array to { categoryId: count } map
            // Handle both camelCase (categoryId) and PascalCase (CategoryId) from API
            const countsMap = {};
            if (Array.isArray(counts)) {
                counts.forEach(item => {
                    const categoryId = item.categoryId || item.CategoryId;
                    const netProductCount = item.netProductCount !== undefined ? item.netProductCount : item.NetProductCount;
                    if (categoryId !== undefined) {
                        countsMap[categoryId] = netProductCount || 0;
                    }
                });
            }
            state.categoryFilters.netProductCounts = countsMap;
        },
        setProducts(state, products) {
            state.products = products;
            state.filteredProductsCache = null; // Clear cache when products change
        },
        setCategorizedProducts(state, categorizedProducts) {
            state.categorizedProducts = categorizedProducts;
        },
        setSelectedCategory(state, category) {
            state.selectedCategory = category;
            state.filteredProductsCache = null; // Clear cache
        },
        setSearchQuery(state, query) {
            state.searchQuery = query;
            state.filteredProductsCache = null; // Clear cache
        },
        setShowAvailableOnly(state, showAvailableOnly) {
            state.showAvailableOnly = showAvailableOnly;
            state.filteredProductsCache = null; // Clear cache
        },
        setSortBy(state, sortBy) {
            state.sortBy = sortBy;
            state.filteredProductsCache = null; // Clear cache
        },
        setSortOrder(state, order) {
            state.sortOrder = order;
            state.filteredProductsCache = null; // Clear cache
        },
        setFilters(state, filters) {
            state.filters = { ...state.filters, ...filters };
            state.filteredProductsCache = null; // Clear cache
        },

        // PWA mutations
        setPwaInstallPromptEvent(state, event) {
            state.pwa.installPromptEvent = event;
            state.pwa.isInstallable = !!event;
        },
        setPwaInstalled(state, isInstalled) {
            state.pwa.isInstalled = isInstalled;
            if (isInstalled) {
                state.pwa.showInstallPrompt = false;
            }
        },
        setPwaUpdateAvailable(state, isAvailable) {
            state.pwa.updateAvailable = isAvailable;
            state.pwa.showUpdateNotification = isAvailable;
        },
        setShowPwaInstallPrompt(state, show) {
            state.pwa.showInstallPrompt = show;
        },
        setShowPwaUpdateNotification(state, show) {
            state.pwa.showUpdateNotification = show;
        }
    },

    getters: {
        getField,
        // Auth getters (needed by components using mapGetters)
        loggedIn: state => state.loggedIn,
        loggedInUser: state => state.loggedInUser,
        isGuestUser: state => state.isGuestUser,

        // Cart getters
        cartItemCount: state => state.cart.items.reduce((count, item) => count + item.quantity, 0),
        cartTotal: state => state.cart.total,
        cartTaxAmount: state => state.cart.taxAmount,
        cartTotalWithTax: state => state.cart.totalWithTax,
        isCartEmpty: state => state.cart.items.length === 0,

        // Declining balance getters
        isDecliningBalanceAvailable: state => state.decliningBalance.isAvailable,
        decliningBalanceAccounts: state => state.decliningBalance.accounts,
        decliningBalanceTotalBalance: state => state.decliningBalance.totalBalance,
        selectedDecliningBalanceAccount: state => {
            return state.decliningBalance.accounts.find(account => account.accountId === state.decliningBalance.selectedAccountId);
        },
        decliningBalanceError: state => state.decliningBalance.error,
        isDecliningBalanceLoading: state => state.decliningBalance.isLoading,
        isDecliningBalancePaymentInProgress: state => state.decliningBalance.paymentInProgress,
        canAffordWithDecliningBalance: state => {
            return state.decliningBalance.totalBalance >= state.cart.total;
        },
        decliningBalanceShortfall: state => {
            return Math.max(0, state.cart.total - state.decliningBalance.totalBalance);
        },
        decliningBalanceRemainingAfterPurchase: state => {
            return Math.max(0, state.decliningBalance.totalBalance - state.cart.total);
        },

        // Ticket operations getters (POS Integration) - Added 2026-01-18
        currentTicket: state => state.ticket.currentTicket,
        lastCreatedTicket: state => state.ticket.lastCreatedTicket,
        lastRefundTicket: state => state.ticket.lastRefundTicket,
        ticketPaymentItems: state => state.ticket.paymentItems,
        ticketError: state => state.ticket.error,
        isTicketLoading: state => state.ticket.isLoading,
        lastTicketOperation: state => state.ticket.lastOperation,
        ticketPaymentItemsTotal: state => {
            return state.ticket.paymentItems.reduce((total, item) => total + item.PaymentAmount, 0);
        },

        // Pickup location getters
        pickupLocations: state => state.pickupLocations.locations,
        selectedPickupLocation: state => state.pickupLocations.selectedLocation,
        isPickupLocationsLoading: state => state.pickupLocations.isLoading,
        pickupLocationsError: state => state.pickupLocations.error,
        hasPickupLocations: state => state.pickupLocations.locations.length > 0,

        // Shipping getters
        shippingConfig: state => state.shipping.config,
        shippingRates: state => state.shipping.rates,
        selectedShippingRate: state => state.shipping.selectedRate,
        isShippingLoading: state => state.shipping.isLoading,
        shippingError: state => state.shipping.error,
        hasShippingRates: state => state.shipping.rates.length > 0,
        isFreeShippingApplied: state => state.shipping.freeShippingApplied,
        shippingAmount: state => state.shipping.selectedRate?.amount || 0,
        freeShippingThreshold: state => state.shipping.config.freeShippingThreshold,
        qualifiesForFreeShipping: state => {
            const threshold = state.shipping.config.freeShippingThreshold;
            return threshold > 0 && state.cart.total >= threshold;
        },
        amountToFreeShipping: state => {
            const threshold = state.shipping.config.freeShippingThreshold;
            if (threshold <= 0) return 0;
            return Math.max(0, threshold - state.cart.total);
        },

        // Category filters getters
        categoryFilterTypes: state => state.categoryFilters.categoryTypes,
        selectedCategoryFilters: state => state.categoryFilters.selectedFilters,
        isCategoryFiltersLoading: state => state.categoryFilters.isLoading,
        isNetProductCountsLoading: state => state.categoryFilters.isCountsLoading,
        hasCategoryFilters: state => state.categoryFilters.categoryTypes.length > 0,
        netProductCounts: state => state.categoryFilters.netProductCounts,
        getSelectedFilterCount: state => {
            return Object.values(state.categoryFilters.selectedFilters)
                .reduce((total, filters) => total + filters.length, 0);
        },
        // Get selected category IDs as comma-separated string for API calls
        selectedCategoryIdsString: state => {
            const allSelected = [];
            Object.values(state.categoryFilters.selectedFilters).forEach(categoryIds => {
                allSelected.push(...categoryIds);
            });
            return allSelected.join(',');
        },

        // Check if a category requires authentication for access
        categoryRequiresAuth: (state) => (categoryId) => {
            // Find the category and check its requiresAuthentication flag
            const category = state.categories.find(c => c.id === categoryId);
            if (category && category.requiresAuthentication) {
                return true;
            }
            return false;
        },

        // Product getters with caching
        filteredProducts: (state, getters) => {
            // Create a cache key based on current filter state
            const currentFilterState = JSON.stringify({
                selectedCategory: state.selectedCategory && state.selectedCategory.id || null,
                searchQuery: state.searchQuery,
                sortBy: state.sortBy,
                sortOrder: state.sortOrder,
                filters: state.filters,
                productsLength: state.products.length,
                isGuestUser: state.isGuestUser // Include guest status in cache key
            });

            // Return cached result if filter state hasn't changed
            if (state.filteredProductsCache && state.lastFilterState === currentFilterState) {
                return state.filteredProductsCache;
            }

            let products = [...state.products];

            // All categories are now guest-accessible with new API system
            // No guest user restrictions needed

            // First, filter to show only products with images (the curated list)
            const productsWithImages = [
                2010609, 2010610, 2010666, 2010757, 2010801, 2010802, 2011017, 2011018,
                2011020, 2011021, 2011034, 2011233, 2011454, 2011455, 2011456, 2011457,
                2011458, 2011459, 2011460, 2011472, 2011473, 2011474, 2011612, 2011613,
                2011614, 2011615, 2011626, 2011627, 2011628
            ];

            // Product image mapping
            const productImages = {
                2010609: 'P 118 Cobalt Blue Tumbler Wolfhead Circle  40 oz.j',
                2010610: 'P 119 White Tumbler Wolfhead  Circle  40 oz.jpg',
                2010666: 'P175 Bowl Soup 24 oz. Coblat Blue.jpg',
                2010757: 'P 118 Cobalt Blue Tumbler Wolfhead Circle  40 oz.j',
                2010801: 'P 310 Glass Tankyard 17.5 oz.jpg',
                2010802: 'P 311 Glass Frosted Wrap design 16 oz.jpg',
                2011017: 'P 526   Mug Etched Military  Green 16 oz.jpg',
                2011018: 'P527 Mug 16 oz Matte San.jpg',
                2011020: 'P 529 Mug  Black Alumni 11 oz.jpg',
                2011021: 'P 530  Mug Black Nursing 11 oz.jpg',
                2011034: 'P543 Ornament.jpg',
                2011233: 'P 742 Sport Bottle Bike Tritan  26 oz Blue.jpg',
                2011454: 'P963 Tumbler Seal White 16 oz.jpg',
                2011455: 'P 964 Insulated 18 oz Grey.jpg',
                2011456: 'P 965  Tumbler White Nursing  20 oz.jpg',
                2011457: 'P 966 Tumbler Blue  Dental Seal 20 oz.jpg',
                2011458: 'p 9949  Tumbler Vertical Logo 22 oz Grey.jpg',
                2011459: 'P968 Tumbler Beach Party 32 oz Blue.jpg',
                2011460: 'P9988 Tumbler Corkster 18 oz White.jpg',
                2011472: 'P 981 Water Bottle  Blue.jpg',
                2011473: 'P 982  Water Bottle SS 32 oz Blue.jpg',
                2011474: 'P 983 Water Bottle SS  Maroon 32 oz.jpg',
                2011612: 'P1121 Glass Candy Etched Jar 16oz.jpg',
                2011613: 'P 1122 Water Bottle Etched Grey w Handle 30 oz.jpg',
                2011614: 'P1123 Frame Pewter Vertical 4x6.jpg',
                2011615: 'p1124 Frame Pewter  Horizontal 4x6.jpg',
                2011626: 'P 1135 Tumbler 20 oz Black Wolf Logo.jpg',
                2011627: 'P 1136 Tumbler Maroon Wolf Logo 20 oz.jpg',
                2011628: 'P 1137 Tumbler 20 oz  Purple Wolf Logo.jpg'
            };

            // Show all products - no longer filtering by image availability
            // Products without images will show placeholder

            // Add image information and availability to products
            // Priority: API imageFilename > hardcoded productImages mapping > null
            products = products.map(product => {
                const imgFilename = product.imageFilename || productImages[product.id] || null;
                return {
                    ...product,
                    imageFilename: imgFilename,
                    hasImage: !!imgFilename,
                    // Set default availability - in a real system this would come from inventory API
                    // For now, we'll set all products as available unless specifically marked otherwise
                    available: product.available !== false // Default to true unless explicitly false
                };
            });

            // Category filtering is now handled by API endpoints
            // Products are pre-filtered by category when fetched from API
            // This client-side filtering is only for search, tags, availability, etc.

            // Filter by search query
            if (state.searchQuery) {
                const query = state.searchQuery.toLowerCase();
                products = products.filter(product =>
                    product.name && product.name.toLowerCase().includes(query) ||
                    (product.description && product.description.toLowerCase().includes(query)) ||
                    (product.tags && product.tags.some(tag => tag.toLowerCase().includes(query)))
                );
            }

            // Filter by tags
            if (state.filters.tags.length > 0) {
                products = products.filter(product =>
                    product.tags && state.filters.tags.some(tag => product.tags.includes(tag))
                );
            }

            // Filter by availability - use new inventory fields (inStock, onHandQuantity)
            // This client-side filter works in conjunction with server-side filtering
            if (state.filters.availability) {
                products = products.filter(product => {
                    // Use new inventory fields if available
                    if (product.inStock !== undefined) {
                        return product.inStock === true;
                    }
                    if (product.onHandQuantity !== undefined) {
                        return product.onHandQuantity > 0;
                    }
                    // Fallback to legacy available field
                    return product.available === undefined || product.available === true;
                });
            }

            // Filter by price range (only if price field exists and is valid)
            if (state.filters.priceRange && state.filters.priceRange.min !== undefined && state.filters.priceRange.max !== undefined) {
                products = products.filter(product => {
                    const price = product.price || 0;
                    return price >= state.filters.priceRange.min && price <= state.filters.priceRange.max;
                });
            }

            // Sort products (handle missing fields gracefully)
            products.sort((a, b) => {
                let comparison = 0;
                if (state.sortBy === 'name') {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    comparison = nameA.localeCompare(nameB);
                } else if (state.sortBy === 'price') {
                    const priceA = a.price || 0;
                    const priceB = b.price || 0;
                    comparison = priceA - priceB;
                }
                return state.sortOrder === 'desc' ? -comparison : comparison;
            });

            // Cache the result
            state.filteredProductsCache = products;
            state.lastFilterState = currentFilterState;

            return products;
        },

        // Group products by base name and price for size variants
        groupedProducts: (state, getters) => {
            const products = getters.filteredProducts;
            const grouped = {};

            products.forEach(product => {
                // Extract size information from product name (handles various formats like "16 oz", "32 oz", etc.)
                const sizeMatch = product.name.match(/(\d+(?:\.\d+)?\s*(?:oz|ounce|ml|liter|l|inch|in|cm|mm)\.?)/i);
                const clothingSizeMatch = product.name.match(/\b(XS|S|M|L|XL|XXL|2X|3X|4X|5X|Small|Medium|Large|Extra Large)\b/i);

                // Also look for color/variant indicators that should be treated as "sizes"
                const colorVariantMatch = product.name.match(/\b(Black|White|Blue|Red|Green|Yellow|Purple|Orange|Pink|Gray|Grey|Maroon|Cobalt|Vertical|Horizontal)\b/i);

                let extractedSize = 'One Size';
                let baseName = product.name;

                // Debug logging for variant detection (can be removed in production)
                // if (sizeMatch || clothingSizeMatch || colorVariantMatch) {
                //     logger.debug(`🔍 Variant detected for "${product.name}":`, {
                //         sizeMatch: sizeMatch ? sizeMatch[1] : null,
                //         clothingSizeMatch: clothingSizeMatch ? clothingSizeMatch[1] : null,
                //         colorVariantMatch: colorVariantMatch ? colorVariantMatch[1] : null
                //     });
                // }

                if (sizeMatch) {
                    // Found volume/measurement size (e.g., "16 oz", "32 oz")
                    extractedSize = sizeMatch[1].replace(/\.$/, ''); // Remove trailing period
                    baseName = product.name.replace(sizeMatch[0], '').replace(/\s+/g, ' ').trim();
                } else if (clothingSizeMatch) {
                    // Found clothing size (e.g., "XL", "Large")
                    extractedSize = clothingSizeMatch[1];
                    baseName = product.name.replace(clothingSizeMatch[0], '').replace(/\s+/g, ' ').trim();
                } else if (colorVariantMatch) {
                    // Found color/variant that should be treated as a size option
                    extractedSize = colorVariantMatch[1];
                    baseName = product.name.replace(colorVariantMatch[0], '').replace(/\s+/g, ' ').trim();
                }

                // Clean up base name by removing common descriptive words that prevent grouping
                baseName = baseName
                    .replace(/\b(Black|White|Blue|Red|Green|Yellow|Purple|Orange|Pink|Gray|Grey|Maroon|Cobalt)\b/gi, '')
                    .replace(/\b(Vertical|Horizontal)\b/gi, '')
                    .replace(/\b(Wolf Logo|wolf Logo)\b/gi, 'Wolf Logo') // Normalize wolf logo
                    .replace(/\s+/g, ' ')
                    .trim();



                const key = `${baseName}_${product.price || 0}`;

                if (!grouped[key]) {
                    grouped[key] = {
                        ...product,
                        baseName: baseName,
                        variants: [],
                        hasVariants: false
                    };
                }

                grouped[key].variants.push({
                    ...product,
                    size: extractedSize,
                    originalName: product.name,
                    // Ensure each variant has proper availability
                    available: product.available !== false
                });

                if (grouped[key].variants.length > 1) {
                    grouped[key].hasVariants = true;
                    grouped[key].name = baseName; // Use base name for grouped products
                }
            });

            const result = Object.values(grouped);

            // Debug logging for grouped products with variants (can be removed in production)
            // const productsWithVariants = result.filter(p => p.hasVariants);
            // if (productsWithVariants.length > 0) {
            //     logger.debug(`📦 Found ${productsWithVariants.length} products with variants:`,
            //         productsWithVariants.map(p => ({
            //             name: p.name,
            //             baseName: p.baseName,
            //             variantCount: p.variants.length,
            //             variants: p.variants.map(v => ({ size: v.size, name: v.originalName }))
            //         }))
            //     );
            // }

            return result;
        },

        // Get available sizes for a product group
        getProductSizes: (state) => (baseName, price) => {
            const key = `${baseName}_${price || 0}`;
            const products = state.products.filter(p => {
                // Extract size information and base name using the same logic as groupedProducts
                const sizeMatch = p.name.match(/(\d+(?:\.\d+)?\s*(?:oz|ounce|ml|liter|l|inch|in|cm|mm)\.?)/i);
                const clothingSizeMatch = p.name.match(/\b(XS|S|M|L|XL|XXL|2X|3X|4X|5X|Small|Medium|Large|Extra Large)\b/i);
                const colorVariantMatch = p.name.match(/\b(Black|White|Blue|Red|Green|Yellow|Purple|Orange|Pink|Gray|Grey|Maroon|Cobalt|Vertical|Horizontal)\b/i);

                let productBaseName = p.name;

                if (sizeMatch) {
                    productBaseName = p.name.replace(sizeMatch[0], '').replace(/\s+/g, ' ').trim();
                } else if (clothingSizeMatch) {
                    productBaseName = p.name.replace(clothingSizeMatch[0], '').replace(/\s+/g, ' ').trim();
                } else if (colorVariantMatch) {
                    productBaseName = p.name.replace(colorVariantMatch[0], '').replace(/\s+/g, ' ').trim();
                }

                // Clean up base name the same way as in groupedProducts
                productBaseName = productBaseName
                    .replace(/\b(Black|White|Blue|Red|Green|Yellow|Purple|Orange|Pink|Gray|Grey|Maroon|Cobalt)\b/gi, '')
                    .replace(/\b(Vertical|Horizontal)\b/gi, '')
                    .replace(/\b(Wolf Logo|wolf Logo)\b/gi, 'Wolf Logo')
                    .replace(/\s+/g, ' ')
                    .trim();

                return productBaseName === baseName && (p.price || 0) === (price || 0);
            });

            return products.map(p => {
                // Extract size using the same improved logic
                const sizeMatch = p.name.match(/(\d+(?:\.\d+)?\s*(?:oz|ounce|ml|liter|l|inch|in|cm|mm)\.?)/i);
                const clothingSizeMatch = p.name.match(/\b(XS|S|M|L|XL|XXL|2X|3X|4X|5X|Small|Medium|Large|Extra Large)\b/i);
                const colorVariantMatch = p.name.match(/\b(Black|White|Blue|Red|Green|Yellow|Purple|Orange|Pink|Gray|Grey|Maroon|Cobalt|Vertical|Horizontal)\b/i);

                let extractedSize = 'One Size';

                if (sizeMatch) {
                    extractedSize = sizeMatch[1].replace(/\.$/, ''); // Remove trailing period
                } else if (clothingSizeMatch) {
                    extractedSize = clothingSizeMatch[1];
                } else if (colorVariantMatch) {
                    extractedSize = colorVariantMatch[1];
                }

                return {
                    size: extractedSize,
                    productId: p.id,
                    available: p.available !== false, // Use actual product availability
                    name: p.name,
                    price: p.price
                };
            });
        },

        // Get product image URL - delegates to unified imageUtils module
        // Supports Cloudflare Images (auto-resizing) and Cloudflare R2 CDN
        // Parameters: productId (required), options (optional: { variant, format })
        // Variants: 'thumb' (200px), 'card' (400px), 'detail' (800px), 'public' (original)
        getProductImageUrl: (state) => (productId, options = {}) => {
            // Look up the product to pass to imageUtils (for imageFilename lookup)
            const product = state.products.find(p => p.id === productId);
            return imageUtilsGetProductImageUrl(productId, { ...options, product });
        },

        // Get category image URL - delegates to unified imageUtils module
        // Parameters: categoryIdOrName (number, string, or object), options (optional)
        getCategoryImageUrl: (state) => (categoryIdOrName, options = {}) => {
            // Pass the categories array for ID lookups
            return imageUtilsGetCategoryImageUrl(categoryIdOrName, { ...options, categories: state.categories });
        },

        // Role-based pricing
        getProductPrice: state => product => {
            if (!state.loggedIn || !state.loggedInUser.role) {
                return product.price; // Guest price
            }

            // Apply role-based pricing
            if (product.rolePricing && product.rolePricing[state.loggedInUser.role]) {
                return product.rolePricing[state.loggedInUser.role];
            }

            return product.price;
        }
    },

    actions: {
        beforeAPI(context){
            context.commit('resetbeforeapi');
        },

        // Authentication actions
        async login({ commit, dispatch }, credentials) {
            commit('resetbeforeapi');
            try {
                // This would integrate with existing login API
                // For now, we'll use the existing login logic
                const response = await callAPI(credentials, 'walletloginuserlimit');

                if (response.Successful) {
                    commit('setLoggedIn', true);
                    commit('setUser', response.user);
                    commit('updateField', { path: 'currentWallet', value: response.wallet });
                    commit('updateField', { path: 'showLoginModal', value: false });

                    // Refresh categories to show all available categories for logged-in user
                    await dispatch('fetchCategories');

                    return { success: true };
                } else {
                    return { success: false, message: response.Message };
                }
            } catch (error) {
                return { success: false, message: error.message };
            }
        },

        logout({ commit }) {
            // Clear user data from store
            commit('logout');
            commit('updateField', { path: 'showLoginModal', value: false });

            // Clear API headers to remove authentication
            const { HTTP } = require('../services/repository');
            HTTP.defaults.headers.post['adminuser'] = 'admin|admin123'; // Reset to default

            // Clear any cached data
            commit('updateField', { path: 'currentWallet', value: {} });
            commit('updateField', { path: 'message', value: '' });
        },

        // Category selection with authentication check
        selectCategory({ commit, state, getters }, category) {
            // Check if user is guest and category requires authentication
            if (state.isGuestUser && getters.categoryRequiresAuth(category.id)) {
                // Show login modal instead of selecting category
                commit('updateField', { path: 'showLoginModal', value: true });
                return { success: false, requiresAuth: true };
            }

            // User is authenticated or category doesn't require auth, proceed
            commit('setSelectedCategory', category);
            return { success: true, requiresAuth: false };
        },

        // Product actions
        async fetchCategories({ commit, state }) {
            commit('resetbeforeapi');
            try {
                // Try enhanced categories API first (Ecommerce_ProductCategory table with images/icons)
                try {
                    const enhancedResponse = await ecommerceApiService.getCategoriesEnhanced(1, true);

                    if (enhancedResponse && enhancedResponse.Successful && enhancedResponse.categories && enhancedResponse.categories.length > 0) {
                        logger.debug(`✅ Enhanced Categories API: ${enhancedResponse.categories.length} categories loaded with icons/images`);

                        // Map enhanced categories with all new fields
                        const categories = enhancedResponse.categories.map(cat => ({
                            id: cat.categoryId,
                            ecommerceCategoryId: cat.ecommerceCategoryId,
                            name: cat.categoryName,
                            slug: cat.categorySlug,
                            code: cat.code,
                            description: cat.description,
                            shortDescription: cat.shortDescription,
                            displayOrder: cat.displayOrder,
                            isFeatured: cat.isFeatured,
                            imageFilename: cat.imageFilename,
                            imageUrl: cat.imageUrl,
                            thumbnailUrl: cat.thumbnailUrl,
                            iconClass: cat.iconClass,
                            backgroundColor: cat.backgroundColor,
                            requiresAuthentication: cat.requiresAuthentication,
                            guestVisible: cat.guestVisible
                        }));

                        commit('setCategories', categories);
                        const locationId = window.PAYMENT_LOCATION_ID || 622103005;
                        commit('updateField', { path: 'loggedInUser.LocationId', value: locationId });
                        return { success: true, locationId: locationId, enhanced: true };
                    }
                } catch (enhancedError) {
                    logger.debug('Enhanced Categories API not available, trying standard API...', enhancedError.message);
                }

                // Fallback to standard database-driven categories API
                const response = await ecommerceApiService.getCategories(window.PAYMENT_LOCATION_ID || 622103005);

                if (response && response.Successful && response.categories && response.categories.length > 0) {
                    logger.debug(`✅ Standard Categories API: ${response.categories.length} categories loaded`);
                    logger.debug('📋 Categories received from API:', response.categories.map(cat => `${cat.categoryId}: ${cat.categoryName}`));

                    // Categories come from database with proper structure
                    const categories = response.categories.map(cat => ({
                        id: cat.categoryId,
                        name: cat.categoryName,
                        displayOrder: cat.displayOrder,
                        productCount: cat.productCount
                    }));

                    commit('setCategories', categories);
                    const locationId = window.PAYMENT_LOCATION_ID || 622103005;
                    commit('updateField', { path: 'loggedInUser.LocationId', value: locationId });
                    return { success: true, locationId: locationId };
                } else {
                    logger.debug('❌ Standard Categories API failed, falling back to legacy system...');
                    // Simple fallback - no complex retry logic needed
                    const locationId = window.PAYMENT_LOCATION_ID || 622103005;
                    const legacyResponse = await ecommerceApiService.getProductTypes(locationId);

                    if (legacyResponse && legacyResponse.Successful && legacyResponse.productTypeList) {
                        logger.debug('✅ Legacy API fallback successful');
                        commit('setCategories', legacyResponse.productTypeList);
                        return { success: true, locationId: locationId };
                    }

                    logger.debug('❌ All category APIs failed');
                    commit('setCategories', []);
                    return { success: false, message: 'No categories found' };
                }
            } catch (error) {
                logger.debug('Categories API error:', error);

                // Handle specific authentication errors
                if (error.message && error.message.includes('Authentication failed')) {
                    logger.error('🚨 API Key authentication failed - check configuration');
                    commit('setCategories', []);
                    return { success: false, message: 'Authentication failed - API key required or invalid' };
                }

                // Simple fallback - no complex retry logic needed
                try {
                    const locationId = window.PAYMENT_LOCATION_ID || 622103005;
                    const legacyResponse = await ecommerceApiService.getProductTypes(locationId);

                    if (legacyResponse && legacyResponse.Successful && legacyResponse.productTypeList) {
                        logger.debug('✅ Legacy API fallback successful');
                        commit('setCategories', legacyResponse.productTypeList);
                        return { success: true, locationId: locationId };
                    }
                } catch (fallbackError) {
                    logger.debug('❌ Legacy API fallback also failed:', fallbackError);
                }

                commit('setCategories', []);
                return { success: false, message: error.message };
            }
        },

        async fetchCategoryFilters({ commit }) {
            commit('setCategoryFiltersLoading', true);
            try {
                const response = await ecommerceApiService.getCategoryFilters();

                if (response && response.Successful) {
                    const categoryTypes = (response.categoryTypes || []).filter(ct => ct.includeAsFilter);
                    commit('setCategoryFilters', {
                        categoryTypes: categoryTypes,
                        totalProducts: response.totalProducts || 0
                    });
                    logger.debug(`✅ Category Filters loaded: ${categoryTypes.length} filter types`);
                    return { success: true, categoryTypes: categoryTypes };
                } else {
                    commit('setCategoryFiltersError', response.message || 'Failed to load filters');
                    return { success: false, message: response.message };
                }
            } catch (error) {
                logger.error('💥 Category Filters fetch error:', error.message);
                commit('setCategoryFiltersError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setCategoryFiltersLoading', false);
            }
        },

        /**
         * Fetch net product counts for category filters based on current selection
         * This updates the counts shown in parentheses next to each filter option
         */
        async fetchNetProductCounts({ commit, getters }) {
            commit('setNetProductCountsLoading', true);
            try {
                const selectedCategoryIds = getters.selectedCategoryIdsString;
                const response = await ecommerceApiService.getNetProductCounts(selectedCategoryIds);

                if (response && response.Successful) {
                    commit('setNetProductCounts', response.categoryCounts || []);
                    logger.debug(`✅ Net Product Counts loaded: ${(response.categoryCounts || []).length} counts`);
                    return { success: true };
                } else {
                    logger.warn('⚠️ Net Product Counts response not successful:', response);
                    return { success: false, message: response.message };
                }
            } catch (error) {
                logger.error('💥 Net Product Counts fetch error:', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setNetProductCountsLoading', false);
            }
        },

        async fetchProducts({ commit, state }, params = {}) {
            commit('resetbeforeapi');
            try {
                const locationId = params.locationId || state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005;

                // NEW CATEGORY-FIRST APPROACH: Use efficient endpoints instead of loading all products
                if (params.categoryId && params.categoryId !== 0) {
                    // Load products for specific category using new efficient endpoint
                    logger.debug(`🔄 Using new category-first API for category: ${params.categoryId}`);
                    return await this.dispatch('fetchProductsByCategory', params.categoryId);
                } else {
                    // Load all products pre-grouped by category using new efficient endpoint
                    logger.debug('🔄 Using new all-categorized products API instead of loading all products');
                    try {
                        const response = await ecommerceApiService.getAllCategorizedProducts(locationId);

                        if (response && response.Successful && response.categorizedProducts) {
                            logger.debug(`✅ New All-Categorized API: ${response.totalProducts} products in ${response.totalCategories} categories loaded successfully`);

                            // Flatten the categorized products into a single array for compatibility
                            const allProducts = [];
                            response.categorizedProducts.forEach(categoryGroup => {
                                if (categoryGroup.products && categoryGroup.products.length > 0) {
                                    allProducts.push(...categoryGroup.products);
                                }
                            });

                            commit('setProducts', allProducts);
                            return { success: true };
                        } else {
                            logger.debug('❌ New All-Categorized API failed, falling back to legacy approach...');
                            // Fallback to legacy approach
                            return await this.dispatch('fetchProductsLegacy', params);
                        }
                    } catch (error) {
                        logger.debug('❌ New All-Categorized API error, falling back to legacy approach...', error);
                        return await this.dispatch('fetchProductsLegacy', params);
                    }
                }
            } catch (error) {
                logger.error('fetchProducts error:', error);
                return { success: false, message: 'Failed to load products' };
            }
        },

        // Legacy product loading method (kept as fallback)
        async fetchProductsLegacy({ commit, state }, params = {}) {
            try {
                const locationId = params.locationId || state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005;

                // Prepare API request parameters
                const apiParams = {
                    locationId,
                    activeOnly: true,
                    availableOnly: params.availableOnly !== undefined ? params.availableOnly : state.showAvailableOnly,
                    ...params
                };

                // If categoryId is provided, use it as productTypeId filter
                if (params.categoryId && params.categoryId !== 0) {
                    apiParams.productTypeId = params.categoryId;
                }

                // Use the legacy eCommerce API (productbylocation_ecommerce)
                const response = await ecommerceApiService.getProductsByLocation(apiParams);

                // Enhanced API should provide proper categorization
                if (!response.Successful) {
                    logger.debug('❌ Legacy Enhanced API failed');
                    return { success: false, message: response.Message || 'Failed to load products' };
                }

                if (response.Successful) {
                    logger.debug(`✅ Legacy Enhanced API: ${response.productList && response.productList.length || 0} products loaded successfully`);
                    commit('setProducts', response.productList);
                    return { success: true };
                } else {
                    logger.debug('❌ Legacy Enhanced API failed:', response.Message || 'Unknown error');
                    return { success: false, message: response.Message || 'Failed to load products' };
                }
            } catch (error) {
                logger.debug('Legacy Products API error:', error);

                // Handle specific authentication errors
                if (error.message && error.message.includes('Authentication failed')) {
                    logger.error('🚨 API Key authentication failed - check configuration');
                    return { success: false, message: 'Authentication failed - API key required or invalid' };
                }

                // Handle 500 errors (likely database/location ID issues)
                if (error.message && error.message.includes('Backend error with location ID')) {
                    logger.error('🚨 Backend database error detected - attempting legacy fallback');
                    try {
                        // Try legacy API with old location ID as emergency fallback
                        const emergencyFallback = await ecommerceApiService.getProductsByLocation({ locationId: 622103005 });
                        if (emergencyFallback.Successful) {
                            logger.debug('✅ Emergency legacy fallback successful');
                            commit('setProducts', emergencyFallback.productList);
                            return { success: true, fallback: 'emergency-legacy' };
                        }
                    } catch (fallbackError) {
                        logger.error('💥 Emergency fallback also failed:', fallbackError);
                    }
                }

                return { success: false, message: error.message };
            }
        },

        async fetchProductsByCategory({ commit, state }, categoryId) {
            commit('resetbeforeapi');
            try {
                const locationId = state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005;

                // Try new category-specific API first
                const response = await ecommerceApiService.getProductsByCategory(categoryId, locationId);

                if (response && response.Successful && response.products) {
                    logger.debug(`✅ New Category API: ${response.products.length} products loaded for category ${categoryId}`);
                    commit('setProducts', response.products);
                    return { success: true };
                } else {
                    logger.debug('❌ New Category API failed, falling back to legacy filtering...');
                    // Fallback to legacy fetchProducts method to avoid circular dependency
                    return await this.dispatch('fetchProductsLegacy', { categoryId, locationId });
                }
            } catch (error) {
                logger.debug('Category Products API error:', error);
                // Fallback to legacy fetchProducts method to avoid circular dependency
                return await this.dispatch('fetchProductsLegacy', { categoryId, locationId: window.PAYMENT_LOCATION_ID || 622103005 });
            }
        },

        async fetchFeaturedProducts({ commit, state }) {
            commit('resetbeforeapi');
            try {
                const locationId = state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005;

                // Try new featured products API
                const response = await ecommerceApiService.getFeaturedProducts(locationId);

                if (response && response.Successful && response.products) {
                    logger.debug(`✅ New Featured Products API: ${response.products.length} featured products loaded`);
                    commit('setProducts', response.products);
                    return { success: true };
                } else {
                    logger.debug('❌ New Featured Products API failed, falling back to legacy method...');
                    // Fallback to legacy fetchProducts method
                    return await this.dispatch('fetchProductsLegacy', { locationId });
                }
            } catch (error) {
                logger.debug('Featured Products API error:', error);

                // Enhanced fallback logic for database issues
                if (error.message && error.message.includes('Backend error with location ID')) {
                    logger.error('🚨 Featured Products: Backend database error detected');
                    logger.debug('🔄 Attempting fallback to legacy products with old location ID');
                    try {
                        // Try with old location ID as emergency fallback
                        return await this.dispatch('fetchProductsLegacy', { locationId: 622103005 });
                    } catch (fallbackError) {
                        logger.error('💥 Featured Products: Emergency fallback failed:', fallbackError);
                    }
                }

                // Standard fallback to legacy fetchProducts
                return await this.dispatch('fetchProductsLegacy', { locationId: window.PAYMENT_LOCATION_ID || 622103005 });
            }
        },

        // NEW: Fetch all products pre-grouped by category (efficient for landing page)
        async fetchAllCategorizedProducts({ commit, state }, params = {}) {
            commit('resetbeforeapi');
            try {
                const locationId = state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005;
                const availableOnly = params.availableOnly !== undefined ? params.availableOnly : state.showAvailableOnly;

                logger.debug('🔄 Fetching all products pre-grouped by category...');
                logger.debug('📦 Inventory filter - availableOnly:', availableOnly);
                const response = await ecommerceApiService.getAllCategorizedProducts(locationId, availableOnly);

                if (response && response.Successful && response.categorizedProducts) {
                    logger.debug(`✅ All Categorized Products API: ${response.totalProducts} products in ${response.totalCategories} categories loaded successfully`);

                    // Store the categorized products structure
                    commit('setCategorizedProducts', response.categorizedProducts);

                    // Also flatten and store as regular products for compatibility
                    const allProducts = [];
                    response.categorizedProducts.forEach(categoryGroup => {
                        if (categoryGroup.products && categoryGroup.products.length > 0) {
                            allProducts.push(...categoryGroup.products);
                        }
                    });
                    commit('setProducts', allProducts);

                    return {
                        success: true,
                        categorizedProducts: response.categorizedProducts,
                        totalProducts: response.totalProducts,
                        totalCategories: response.totalCategories
                    };
                } else {
                    logger.debug('❌ All Categorized Products API failed, falling back to legacy method...');
                    // Fallback to legacy approach
                    return await this.dispatch('fetchProductsLegacy', { locationId });
                }
            } catch (error) {
                logger.debug('All Categorized Products API error:', error);
                // Fallback to legacy approach
                return await this.dispatch('fetchProductsLegacy', { locationId: window.PAYMENT_LOCATION_ID || 622103005 });
            }
        },

        // NEW: Fetch products with size/variation grouping (fixes duplicate product display)
        // Supports filtering by ProductCategoryIDs via categoryIds parameter (comma-separated string)
        async fetchGroupedProducts({ commit, state, getters }, params = {}) {
            commit('resetbeforeapi');
            try {
                const locationId = params.locationId || state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005;
                const categoryId = params.categoryId || null;
                const availableOnly = params.availableOnly !== undefined ? params.availableOnly : state.showAvailableOnly;

                // Support new category filter system - get selected category IDs from filters
                // Can be passed directly as params.categoryIds or retrieved from store
                let categoryIds = params.categoryIds || null;
                if (!categoryIds && getters.selectedCategoryIdsString) {
                    categoryIds = getters.selectedCategoryIdsString;
                }

                logger.debug('🔄 Fetching grouped products with size variations...');
                logger.debug('📦 Inventory filter - availableOnly:', availableOnly);
                if (categoryIds) {
                    logger.debug('🔍 Category filter - categoryIds:', categoryIds);
                }
                const response = await ecommerceApiService.getGroupedProducts(locationId, categoryId, availableOnly, categoryIds);

                if (response && response.Successful && response.products) {
                    logger.debug(`✅ Grouped Products API: ${response.productCount} grouped products with ${response.totalVariations} total variations loaded successfully`);
                    if (response.categoryFilter) {
                        logger.debug(`🔍 Applied category filter: ${response.categoryFilter}`);
                    }

                    // Store the grouped products
                    commit('setProducts', response.products);
                    return { success: true, data: response };
                } else {
                    logger.debug('❌ Grouped Products API failed, falling back to legacy approach...');
                    return await this.dispatch('fetchProductsLegacy', { locationId, categoryId });
                }
            } catch (error) {
                logger.error('fetchGroupedProducts error:', error);
                return await this.dispatch('fetchProductsLegacy', { locationId: params.locationId || window.PAYMENT_LOCATION_ID || 622103005 });
            }
        },

        // Cart actions with automatic persistence
        async addProductToCart({ commit, dispatch, getters, rootState }, product) {
            // 🔍 DEBUGGING: Log product processing in store action
            logger.group('🛒 STORE ACTION: addProductToCart');
            logger.debug('📦 Original product received:', {
                id: product.id,
                name: product.name,
                price: product.price,
                productId: product.productId,
                fullProduct: product
            });

            const priceToUse = getters.getProductPrice(product);
            logger.debug('💰 Price calculation:', {
                originalPrice: product.price,
                calculatedPrice: priceToUse,
                priceSource: 'getProductPrice getter'
            });

            const productForCart = { ...product, price: priceToUse };
            logger.debug('🛍️ Product being sent to cart mutation:', {
                id: productForCart.id,
                name: productForCart.name,
                price: productForCart.price,
                productId: productForCart.productId,
                fullProduct: productForCart
            });
            logger.groupEnd();

            commit('addToCart', productForCart);

            // Save cart to storage after adding item
            await dispatch('saveCartToStorage');

            // Trigger cart button animation
            if (typeof window !== 'undefined' && window.Vue) {
                // Use Vue's global event bus to trigger cart animation
                setTimeout(() => {
                    const app = document.getElementById('app').__vue__
                    if (app && app.$root) {
                        app.$root.$emit('animate-cart')
                    }
                }, 50)
            }
        },

        async removeProductFromCart({ commit, dispatch }, productId) {
            commit('removeFromCart', productId);
            await dispatch('saveCartToStorage');
        },

        async updateProductQuantity({ commit, dispatch }, { productId, quantity }) {
            commit('updateCartQuantity', { productId, quantity });
            await dispatch('saveCartToStorage');
        },

        async clearShoppingCart({ commit }) {
            commit('clearCart');
            // Note: Don't auto-save when clearing cart, this is usually intentional
        },

        async setCartTaxInformation({ commit, dispatch }, { taxAmount, totalWithTax }) {
            commit('setTaxInformation', { taxAmount, totalWithTax });
            await dispatch('saveCartToStorage');
        },

        // Cart persistence actions
        async saveCartToStorage({ state }) {
            try {
                const school = getCurrentSchool();
                const cartData = {
                    version: CART_STORAGE_VERSION, // Track storage version for cache invalidation
                    items: state.cart.items || [],
                    total: state.cart.total || 0,
                    guestInfo: state.cart.guestInfo || {},
                    taxAmount: state.cart.taxAmount || 0,
                    totalWithTax: state.cart.totalWithTax || 0,
                    school: school, // Store which school this cart belongs to
                    timestamp: Date.now()
                };

                // Use school-specific keys for cart isolation
                const cartKey = getSchoolCartKey();
                const timestampKey = getSchoolCartKey('timestamp');

                localStorage.setItem(cartKey, JSON.stringify(cartData));
                localStorage.setItem(timestampKey, Date.now().toString());
                logger.debug(`🛒 Cart saved to localStorage for school "${school}":`, {
                    itemCount: cartData.items.length,
                    total: cartData.total,
                    key: cartKey
                });
            } catch (error) {
                logger.error('💥 Failed to save cart to storage:', error);
            }
        },

        async restoreCartFromStorage({ commit }) {
            try {
                const currentSchool = getCurrentSchool();
                const cartKey = getSchoolCartKey();
                const timestampKey = getSchoolCartKey('timestamp');

                const storedCart = localStorage.getItem(cartKey);
                const timestamp = localStorage.getItem(timestampKey);

                if (!storedCart || !timestamp) {
                    logger.debug(`🛒 No cart data found for school "${currentSchool}"`);
                    return false;
                }

                const cartAge = Date.now() - parseInt(timestamp);
                const maxAge = 24 * 60 * 60 * 1000; // 24 hours

                if (cartAge > maxAge) {
                    logger.debug(`🛒 Cart data expired for school "${currentSchool}", clearing`);
                    localStorage.removeItem(cartKey);
                    localStorage.removeItem(timestampKey);
                    return false;
                }

                const cartData = JSON.parse(storedCart);

                // Validate cart structure
                if (!cartData.items || !Array.isArray(cartData.items)) {
                    logger.warn(`🛒 Invalid cart structure for school "${currentSchool}", clearing`);
                    localStorage.removeItem(cartKey);
                    localStorage.removeItem(timestampKey);
                    return false;
                }

                // Additional validation: Ensure cart belongs to current school
                if (cartData.school && cartData.school !== currentSchool) {
                    logger.warn(`🛒 Cart mismatch: stored for "${cartData.school}" but current is "${currentSchool}", ignoring`);
                    return false;
                }

                // Version check: Clear stale tax/shipping data from old versions
                // This fixes the bug where shipping amount was cached as tax amount
                if (!cartData.version || cartData.version < CART_STORAGE_VERSION) {
                    logger.warn(`🛒 Cart version mismatch (stored: ${cartData.version || 'none'}, current: ${CART_STORAGE_VERSION}), clearing stale tax data`);
                    // Keep cart items but reset tax/total data that may be stale
                    cartData.taxAmount = 0;
                    cartData.totalWithTax = 0;
                    cartData.version = CART_STORAGE_VERSION;
                }

                commit('restoreCart', cartData);
                logger.debug('🛒 Cart restored from localStorage:', {
                    itemCount: cartData.items.length,
                    total: cartData.total,
                    age: Math.round(cartAge / 1000 / 60) + ' minutes'
                });
                return true;
            } catch (error) {
                logger.error('💥 Failed to restore cart from storage:', error);
                // Clean up corrupted data
                try {
                    localStorage.removeItem('ecommerce-cart');
                    localStorage.removeItem('ecommerce-cart-timestamp');
                } catch (cleanupError) {
                    logger.error('💥 Failed to cleanup corrupted cart data:', cleanupError);
                }
                return false;
            }
        },

        async recoverCartAdvanced({ commit }, transactionReference = null) {
            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const cartData = await ecommerceApiService.recoverCart(transactionReference);

                if (cartData) {
                    commit('restoreCart', cartData);
                    return true;
                }
                return false;
            } catch (error) {
                logger.error('💥 Failed to recover cart:', error);
                return false;
            }
        },

        async recoverCartFromPaymentReturn({ commit }) {
            try {
                // Use dynamic import to avoid bundler issues with static import
                // The { default: apiService } pattern extracts the default export correctly
                const { default: apiService } = await import('../services/ecommerceApi');
                const cartData = await apiService.recoverCartFromPaymentReturn();

                if (cartData) {
                    commit('restoreCart', cartData);
                    return true;
                }
                return false;
            } catch (error) {
                logger.error('💥 Failed to recover cart from payment return:', error);
                return false;
            }
        },

        async clearCartFromStorage({ commit }) {
            try {
                const currentSchool = getCurrentSchool();
                const cartKey = getSchoolCartKey();
                const timestampKey = getSchoolCartKey('timestamp');

                // Clear school-specific cart data
                localStorage.removeItem(cartKey);
                localStorage.removeItem(timestampKey);
                localStorage.removeItem('ecommerce-payment-in-progress');

                // Also clear old non-scoped keys for backward compatibility
                localStorage.removeItem('ecommerce-cart');
                localStorage.removeItem('ecommerce-cart-timestamp');

                commit('clearCart');
                logger.debug(`🛒 Cart cleared from localStorage for school "${currentSchool}"`);
            } catch (error) {
                logger.error('💥 Failed to clear cart from storage:', error);
            }
        },

        async markPaymentInProgress() {
            try {
                localStorage.setItem('ecommerce-payment-in-progress', Date.now().toString());
                logger.debug('💳 Payment marked as in progress');
            } catch (error) {
                logger.error('💥 Failed to mark payment in progress:', error);
            }
        },

        async clearPaymentInProgress() {
            try {
                localStorage.removeItem('ecommerce-payment-in-progress');
                logger.debug('💳 Payment in progress flag cleared');
            } catch (error) {
                logger.error('💥 Failed to clear payment in progress:', error);
            }
        },

        // Email validation actions
        async checkEmailExists({ commit }, email) {
            if (!email || email.trim() === '') {
                commit('clearEmailValidation');
                return { success: false, error: 'Email is required' };
            }

            commit('setEmailChecking', true);

            try {
                const { ecommerceApiService } = await import('../services/ecommerceApi');
                const result = await ecommerceApiService.checkEmail(email.trim());

                if (result.success) {
                    commit('setEmailCheckResult', {
                        email: email.trim(),
                        exists: result.EmailExists,
                        userInfo: result.EmailExists ? {
                            personId: result.PersonId,
                            email: result.Email,
                            userName: result.UserName,
                            isActive: result.IsActive
                        } : null,
                        error: null
                    });

                    return {
                        success: true,
                        emailExists: result.EmailExists,
                        userInfo: result.EmailExists ? {
                            personId: result.PersonId,
                            email: result.Email,
                            userName: result.UserName,
                            isActive: result.IsActive
                        } : null
                    };
                } else {
                    commit('setEmailCheckResult', {
                        email: email.trim(),
                        exists: false,
                        userInfo: null,
                        error: result.error
                    });

                    return { success: false, error: result.error };
                }
            } catch (error) {
                logger.error('Email check error:', error);
                commit('setEmailCheckResult', {
                    email: email.trim(),
                    exists: false,
                    userInfo: null,
                    error: error.message || 'Failed to check email'
                });

                return { success: false, error: error.message || 'Failed to check email' };
            } finally {
                commit('setEmailChecking', false);
            }
        },

        clearEmailValidationState({ commit }) {
            commit('clearEmailValidation');
        },

        // Declining balance actions
        async checkDecliningBalance({ commit, state }) {
            if (!state.loggedIn || !state.loggedInUser.user_personId) {
                commit('setDecliningBalanceAvailable', false);
                return { success: false, message: 'User not authenticated' };
            }

            commit('setDecliningBalanceLoading', true);
            commit('setDecliningBalanceError', null);

            try {
                const { ecommerceApiService, decliningBalanceUtils } = await import('../services/ecommerceApi');

                const personId = decliningBalanceUtils.getAuthenticatedPersonId(state.loggedInUser);
                const locationId = decliningBalanceUtils.getAuthenticatedLocationId(state.loggedInUser);

                if (!personId || !locationId) {
                    throw new Error('Missing required user data for declining balance check');
                }

                const response = await ecommerceApiService.checkUserBalance({
                    personId,
                    locationId
                });

                if (response.Successful && response.accounts && response.accounts.length > 0) {
                    commit('setDecliningBalanceAccounts', response.accounts);
                    commit('setDecliningBalanceAvailable', true);

                    // Auto-select the first account if none selected
                    if (!state.decliningBalance.selectedAccountId && response.accounts.length > 0) {
                        commit('setSelectedDecliningBalanceAccount', response.accounts[0].accountId);
                    }

                    return { success: true, balance: response.totalBalance };
                } else {
                    commit('setDecliningBalanceAvailable', false);
                    return { success: false, message: response.message || 'No declining balance accounts found' };
                }
            } catch (error) {
                logger.error('Declining balance check failed:', error);
                commit('setDecliningBalanceError', error.message);
                commit('setDecliningBalanceAvailable', false);
                return { success: false, message: error.message };
            } finally {
                commit('setDecliningBalanceLoading', false);
            }
        },

        async processDecliningBalancePayment({ commit, state, getters }, options = {}) {
            if (!state.loggedIn || !state.decliningBalance.isAvailable) {
                return { success: false, message: 'Declining balance not available' };
            }

            if (!getters.canAffordWithDecliningBalance) {
                return { success: false, message: `Insufficient funds. You need $${getters.decliningBalanceShortfall.toFixed(2)} more.` };
            }

            commit('setDecliningBalancePaymentInProgress', true);
            commit('setDecliningBalanceError', null);

            try {
                const { ecommerceApiService, decliningBalanceUtils } = await import('../services/ecommerceApi');

                const selectedAccount = getters.selectedDecliningBalanceAccount;
                if (!selectedAccount) {
                    throw new Error('No declining balance account selected');
                }

                const paymentRequest = {
                    personId: decliningBalanceUtils.getAuthenticatedPersonId(state.loggedInUser),
                    accountId: selectedAccount.accountId,
                    amount: state.cart.total,
                    orderReference: decliningBalanceUtils.generateOrderReference(),
                    description: 'Ecommerce Purchase',
                    items: decliningBalanceUtils.formatCartItemsForPayment(state.cart.items),
                    pickupLocation: options.pickupLocation || state.cart.selectedPickupLocation
                };

                const response = await ecommerceApiService.processDecliningBalancePayment(paymentRequest);

                if (response.Successful) {
                    commit('setDecliningBalancePaymentResult', response);

                    // Store tax from ticket if backend returned it (Ticket.TaxAmount)
                    if (response.taxAmount > 0) {
                        commit('setTaxInformation', {
                            taxAmount: response.taxAmount,
                            totalWithTax: response.totalAmount || (state.cart.total + response.taxAmount)
                        });
                    }

                    // Update account balance
                    const updatedAccounts = state.decliningBalance.accounts.map(account => {
                        if (account.accountId === selectedAccount.accountId) {
                            return { ...account, currentBalance: response.remainingBalance };
                        }
                        return account;
                    });
                    commit('setDecliningBalanceAccounts', updatedAccounts);

                    return {
                        success: true,
                        transactionId: response.transactionId,
                        remainingBalance: response.remainingBalance,
                        orderReference: response.orderReference,
                        taxAmount: response.taxAmount || 0
                    };
                } else {
                    throw new Error(response.message || 'Payment processing failed');
                }
            } catch (error) {
                logger.error('Declining balance payment failed:', error);
                commit('setDecliningBalanceError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setDecliningBalancePaymentInProgress', false);
            }
        },

        clearDecliningBalanceData({ commit }) {
            commit('clearDecliningBalanceData');
        },

        // =========================================================================
        // TICKET OPERATIONS (POS Integration) - Added 2026-01-18
        // =========================================================================

        /**
         * Create a ticket with payment for a single product purchase
         * @param {Object} request - Ticket creation request (see ecommerceApi.createTicket)
         * @returns {Object} Result with success status and ticket data
         */
        async createTicket({ commit }, request) {
            commit('setTicketLoading', true);
            commit('setTicketError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.createTicket(request);

                if (response.Successful) {
                    commit('setLastCreatedTicket', response);
                    commit('setCurrentTicket', response);
                    logger.debug('🎫 Vuex: Ticket created successfully', response);
                    return { success: true, ticket: response };
                } else {
                    throw new Error(response.Message || 'Failed to create ticket');
                }
            } catch (error) {
                logger.error('🎫 Vuex: Ticket creation failed', error);
                commit('setTicketError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setTicketLoading', false);
            }
        },

        /**
         * Add a payment item to an existing ticket (for split payments)
         * @param {Object} payload - { ticketId, request }
         * @returns {Object} Result with success status and payment item data
         */
        async addTicketPayment({ commit }, { ticketId, request }) {
            commit('setTicketLoading', true);
            commit('setTicketError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.addTicketPaymentItem(ticketId, request);

                if (response.Successful) {
                    commit('addTicketPaymentItem', response);
                    logger.debug('🎫 Vuex: Payment item added successfully', response);
                    return { success: true, paymentItem: response };
                } else {
                    throw new Error(response.Message || 'Failed to add payment item');
                }
            } catch (error) {
                logger.error('🎫 Vuex: Add payment item failed', error);
                commit('setTicketError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setTicketLoading', false);
            }
        },

        /**
         * Create a refund ticket for an existing purchase
         * @param {Object} request - Refund request (see ecommerceApi.createRefundTicket)
         * @returns {Object} Result with success status and refund ticket data
         */
        async createRefundTicket({ commit }, request) {
            commit('setTicketLoading', true);
            commit('setTicketError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.createRefundTicket(request);

                if (response.Successful) {
                    commit('setLastRefundTicket', response);
                    logger.debug('🎫 Vuex: Refund ticket created successfully', response);
                    return { success: true, refundTicket: response };
                } else {
                    throw new Error(response.Message || 'Failed to create refund ticket');
                }
            } catch (error) {
                logger.error('🎫 Vuex: Refund ticket creation failed', error);
                commit('setTicketError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setTicketLoading', false);
            }
        },

        /**
         * Update guest information
         * @param {Object} request - Guest update request (see ecommerceApi.updateGuest)
         * @returns {Object} Result with success status and guest data
         */
        async updateGuest({ commit }, request) {
            commit('setTicketLoading', true);
            commit('setTicketError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.updateGuest(request);

                if (response.Successful) {
                    logger.debug('👤 Vuex: Guest updated successfully', response);
                    return { success: true, guest: response };
                } else {
                    throw new Error(response.Message || 'Failed to update guest');
                }
            } catch (error) {
                logger.error('👤 Vuex: Guest update failed', error);
                commit('setTicketError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setTicketLoading', false);
            }
        },

        /**
         * Get ticket details by ID
         * @param {number} ticketId - The ticket ID
         * @returns {Object} Result with success status and ticket details
         */
        async getTicketDetails({ commit }, ticketId) {
            commit('setTicketLoading', true);
            commit('setTicketError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.getTicketDetails(ticketId);

                if (response.Successful) {
                    commit('setCurrentTicket', response.ticket);
                    logger.debug('🎫 Vuex: Ticket details fetched', response);
                    return { success: true, ticket: response.ticket };
                } else {
                    throw new Error(response.Message || 'Failed to get ticket details');
                }
            } catch (error) {
                logger.error('🎫 Vuex: Get ticket details failed', error);
                commit('setTicketError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setTicketLoading', false);
            }
        },

        /**
         * Clear all ticket data from state
         */
        clearTicketData({ commit }) {
            commit('clearTicketData');
        },

        // =========================================================================
        // END TICKET OPERATIONS
        // =========================================================================

        // WorldPay hosted payment integration
        async initiateWorldPayPayment({ commit, state, dispatch }, options = {}) {
            try {
                logger.debug('🔄 Vuex: Initiating WorldPay payment...');

                // Mark payment as in progress to preserve cart during payment flow
                await dispatch('markPaymentInProgress');

                // Import the payment utilities
                const { ecommerceApiService, paymentUtils } = await import('../services/ecommerceApi');

                // Extract guestInfo, pickupLocation, and recaptchaToken from options
                const guestInfo = options.guestInfo || null;
                const pickupLocation = options.pickupLocation || state.cart.selectedPickupLocation;
                const recaptchaToken = options.recaptchaToken || '';

                // SHIPPING FIX: Include shipping amount in the total
                const shippingAmount = state.shipping.selectedRate?.amount || 0;
                const shippingInfo = state.shipping.selectedRate ? {
                    rateId: state.shipping.selectedRate.id,
                    carrier: state.shipping.selectedRate.carrier,
                    service: state.shipping.selectedRate.service,
                    amount: shippingAmount,
                    address: options.shippingAddress || null
                } : null;

                // Total including shipping (tax will be added by backend)
                const totalWithShipping = state.cart.total + shippingAmount;

                logger.debug('🚚 Shipping info:', { shippingAmount, shippingInfo, cartTotal: state.cart.total, totalWithShipping });

                // Create payment request
                const paymentRequest = paymentUtils.createPaymentRequest({
                    cartItems: state.cart.items,
                    cartTotal: totalWithShipping, // FIXED: Include shipping in total
                    guestInfo: guestInfo,
                    locationId: state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005,
                    pickupLocation: pickupLocation,
                    shipping: shippingInfo // FIXED: Pass shipping info to backend
                });

                logger.debug('🔄 WorldPay Payment Request:', paymentRequest);

                // Call backend to initiate payment (with reCAPTCHA token)
                const response = await ecommerceApiService.initiatePayment(paymentRequest, recaptchaToken);

                logger.debug('✅ WorldPay Payment Response:', response);

                if (response.Successful && response.paymentUrl) {
                    // Store tax information from backend response
                    // Backend returns taxAmount directly from ProcessTicket (Ticket.TaxAmount)
                    const backendTaxAmount = response.taxAmount || 0;
                    const backendSubtotal = response.subtotal || state.cart.total;
                    const backendShipping = response.shippingAmount || 0;
                    const backendTotal = response.totalAmount || (backendSubtotal + backendTaxAmount + backendShipping);

                    if (backendTaxAmount > 0 || response.ticketId) {
                        await dispatch('setCartTaxInformation', {
                            taxAmount: backendTaxAmount,
                            totalWithTax: backendTotal
                        });
                        logger.debug('💰 Tax information from backend (Ticket.TaxAmount):', {
                            subtotal: backendSubtotal,
                            tax: backendTaxAmount,
                            shipping: backendShipping,
                            total: backendTotal
                        });
                    }

                    return {
                        success: true,
                        paymentUrl: response.paymentUrl,
                        transactionId: response.transactionId,
                        ticketId: response.ticketId,
                        totalAmount: backendTotal,
                        taxAmount: backendTaxAmount,
                        subtotal: backendSubtotal,
                        shippingAmount: backendShipping,
                        message: response.message || 'Payment URL generated successfully'
                    };
                } else {
                    throw new Error(response.message || 'Failed to generate payment URL');
                }

            } catch (error) {
                logger.error('💥 WorldPay payment initiation failed:', error);
                return {
                    success: false,
                    message: error.message || 'Payment initiation failed',
                    error: error
                };
            }
        },

        // Pickup location actions
        async fetchPickupLocations({ commit, state }) {
            // Check if we already have recent data
            if (state.pickupLocations.locations.length > 0 && state.pickupLocations.lastFetched) {
                const lastFetched = new Date(state.pickupLocations.lastFetched);
                const now = new Date();
                const timeDiff = now - lastFetched;
                const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

                if (timeDiff < fiveMinutes) {
                    logger.debug('🔄 Using cached pickup locations (fetched less than 5 minutes ago)');
                    return { success: true, cached: true };
                }
            }

            commit('setPickupLocationsLoading', true);
            commit('setPickupLocationsError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.getPickupLocations();

                if (response.Successful) {
                    commit('setPickupLocations', response.pickupLocations);
                    logger.debug(`✅ Pickup locations loaded: ${response.pickupLocations.length} locations`);

                    // Console log for email notifications (as requested)
                    logger.debug('📧 [NOTIFICATION] Pickup locations updated - would send notification to admin');

                    return { success: true, locations: response.pickupLocations };
                } else {
                    const errorMessage = response.Message || 'Failed to load pickup locations';
                    commit('setPickupLocationsError', errorMessage);
                    logger.error('❌ Failed to load pickup locations:', errorMessage);
                    return { success: false, message: errorMessage };
                }
            } catch (error) {
                const errorMessage = error.message || 'Failed to load pickup locations';
                commit('setPickupLocationsError', errorMessage);
                logger.error('💥 Pickup locations error:', error);
                return { success: false, message: errorMessage };
            } finally {
                commit('setPickupLocationsLoading', false);
            }
        },

        selectPickupLocation({ commit }, location) {
            commit('setSelectedPickupLocation', location);
            logger.debug('📍 Pickup location selected:', location);

            // Console log for email notifications (as requested)
            if (location) {
                logger.debug(`📧 [NOTIFICATION] Customer selected pickup location: ${location.name} - would send notification to location and customer`);
            }

            return { success: true };
        },

        clearPickupLocationSelection({ commit }) {
            commit('setSelectedPickupLocation', null);
            logger.debug('📍 Pickup location selection cleared');
            return { success: true };
        },

        // Shipping actions
        async fetchShippingConfig({ commit }) {
            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');
                const response = await ecommerceApiService.getShippingConfig();

                if (response.Successful || response.isEnabled !== undefined) {
                    commit('setShippingConfig', {
                        isEnabled: response.isEnabled,
                        provider: response.provider || 'internal',
                        flatRateAmount: response.flatRateAmount || 0,
                        freeShippingThreshold: response.freeShippingThreshold || 0,
                        defaultMethod: response.defaultMethod || 'flat_rate'
                    });
                    logger.debug('✅ Shipping config loaded:', response);
                    return { success: true, config: response };
                } else {
                    logger.debug('❌ Shipping config failed:', response.Message);
                    return { success: false, message: response.Message };
                }
            } catch (error) {
                logger.error('💥 Shipping config error:', error);
                return { success: false, message: error.message };
            }
        },

        async fetchShippingQuotes({ commit, state }, shippingAddress) {
            if (!shippingAddress || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zipCode) {
                return { success: false, message: 'Incomplete shipping address' };
            }

            commit('setShippingLoading', true);
            commit('setShippingError', null);

            try {
                const { default: ecommerceApiService } = await import('../services/ecommerceApi');

                // Build quote request
                const quoteRequest = {
                    salonId: state.loggedInUser.LocationId || window.PAYMENT_LOCATION_ID || 622103005,
                    toAddress: {
                        name: `${shippingAddress.firstName || ''} ${shippingAddress.lastName || ''}`.trim(),
                        street1: shippingAddress.line1,
                        street2: shippingAddress.line2 || '',
                        city: shippingAddress.city,
                        state: shippingAddress.state,
                        postalCode: shippingAddress.zipCode,
                        country: shippingAddress.country || 'US',
                        isResidential: true
                    },
                    items: state.cart.items
                        .filter(item => item && (item.productId || item.id)) // Filter out invalid items
                        .map(item => ({
                            // Use productId (database ID) if available, fall back to id
                            productId: parseInt(item.productId || item.id, 10) || 0,
                            productName: item.name || 'Unknown Product',
                            quantity: parseInt(item.quantity, 10) || 1,
                            unitPrice: parseFloat(item.price) || 0,
                            requiresShipping: true
                        }))
                        .filter(item => item.productId > 0), // Ensure valid productId
                    orderSubtotal: state.cart.total
                };

                logger.debug('🚚 Fetching shipping quotes:', quoteRequest);

                // Validate request has items before calling API
                if (!quoteRequest.items || quoteRequest.items.length === 0) {
                    logger.warn('⚠️ No valid items in cart for shipping quote');
                    commit('setShippingError', 'No shippable items in cart');
                    return { success: false, message: 'No shippable items in cart' };
                }

                const response = await ecommerceApiService.getShippingQuotes(quoteRequest);

                if (response.Successful) {
                    commit('setShippingRates', {
                        rates: response.rates || [],
                        freeShippingApplied: response.freeShippingApplied || false
                    });

                    // Auto-select first rate if available
                    if (response.rates && response.rates.length > 0) {
                        commit('setSelectedShippingRate', response.rates[0]);
                    }

                    logger.debug(`✅ Shipping quotes loaded: ${response.rates?.length || 0} rates`);
                    return { success: true, rates: response.rates };
                } else {
                    commit('setShippingError', response.Message || 'Failed to get shipping quotes');
                    return { success: false, message: response.Message };
                }
            } catch (error) {
                logger.error('💥 Shipping quotes error:', error);
                commit('setShippingError', error.message);
                return { success: false, message: error.message };
            } finally {
                commit('setShippingLoading', false);
            }
        },

        selectShippingRate({ commit }, rate) {
            commit('setSelectedShippingRate', rate);
            // Clear stale tax data - tax will be recalculated at checkout with new shipping
            commit('clearTaxInformation');
            logger.debug('🚚 Shipping rate selected:', rate);
            return { success: true };
        },

        clearShippingSelection({ commit }) {
            commit('clearShippingData');
            logger.debug('🚚 Shipping selection cleared');
            return { success: true };
        },

        // PWA actions
        showUpdateAvailable({ commit }) {
            logger.debug('[PWA] Update available notification triggered');
            commit('setPwaUpdateAvailable', true);
        },

        hideUpdateNotification({ commit }) {
            commit('setShowPwaUpdateNotification', false);
        },

        captureInstallPrompt({ commit }, event) {
            logger.debug('[PWA] Install prompt event captured');
            commit('setPwaInstallPromptEvent', event);
        },

        showInstallPrompt({ commit, state }) {
            if (state.pwa.isInstallable && !state.pwa.isInstalled) {
                commit('setShowPwaInstallPrompt', true);
            }
        },

        hideInstallPrompt({ commit }) {
            commit('setShowPwaInstallPrompt', false);
        },

        async installPwa({ commit, state }) {
            if (!state.pwa.installPromptEvent) {
                logger.warn('[PWA] No install prompt event available');
                return { success: false, message: 'Install prompt not available' };
            }

            try {
                const promptEvent = state.pwa.installPromptEvent;
                const result = await promptEvent.prompt();
                logger.debug('[PWA] Install prompt result:', result);

                const choiceResult = await promptEvent.userChoice;
                logger.debug('[PWA] User choice:', choiceResult.outcome);

                if (choiceResult.outcome === 'accepted') {
                    commit('setPwaInstalled', true);
                    commit('setPwaInstallPromptEvent', null);
                    return { success: true, installed: true };
                } else {
                    commit('setShowPwaInstallPrompt', false);
                    return { success: true, installed: false };
                }
            } catch (error) {
                logger.error('[PWA] Install error:', error);
                return { success: false, message: error.message };
            }
        },

        dismissInstallPrompt({ commit }) {
            commit('setShowPwaInstallPrompt', false);
            commit('setPwaInstallPromptEvent', null);
        }
    }
})
// Cache bust: 2026-01-27T10:45:00Z
